import { useState } from 'react'
import { getCountries, getCountryCallingCode, isValidPhoneNumber, type CountryCode } from 'libphonenumber-js'
import { Link, useNavigate } from 'react-router-dom'
import { AuthAside } from '../components/AuthAside'
import { useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../lib/api'
import { useGsap } from '../lib/useGsap'
import { useToast } from '../components/Toast'
import gsap from 'gsap'

function useAuthAnimation() {
  return useGsap(({ el }) => {
    gsap.from(el.querySelectorAll('.anim'), {
      y: 14, opacity: 0, duration: 0.4, stagger: 0.05, ease: 'power2.out',
    })
  }, [])
}

/** A 6-digit code input, styled once and reused by every verify screen. */
function CodeInput({ id, value, onChange, autoFocus }: {
  id: string; value: string; onChange: (v: string) => void; autoFocus?: boolean
}) {
  return (
    <input id={id} inputMode="numeric" maxLength={6} placeholder="••••••" autoFocus={autoFocus}
           value={value} onChange={e => onChange(e.target.value.replace(/\D/g, ''))}
           className="code-input" autoComplete="one-time-code" />
  )
}

export function Login() {
  const ref = useAuthAnimation()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const toast = useToast()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [forgot, setForgot] = useState(false)
  const [sent, setSent] = useState(false)
  const [needCode, setNeedCode] = useState(false)
  const [code, setCode] = useState('')
  // Unverified accounts can't sign in; the server sends a fresh code and we prompt for it.
  const [emailVerify, setEmailVerify] = useState(false)
  const [remember, setRemember] = useState(true)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    try {
      await api.auth.login({ email, password, code: needCode ? code : undefined, remember })
      await qc.invalidateQueries({ queryKey: ['me'] })
      navigate('/', { replace: true })
    } catch (err) {
      const e2 = err as ApiError
      // First factor was fine; the server is now asking for the authenticator code.
      if (e2.code === 'mfa_required') { setNeedCode(true) }
      else if (e2.code === 'email_verification_required') {
        setEmailVerify(true); setCode('')
        toast.info(`We’ve sent a 6-digit code to ${email}.`)
      }
      else toast.error(e2.message || 'Could not sign in.')
    } finally {
      setBusy(false)
    }
  }

  async function verifyEmailNow(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    try {
      await api.auth.verifyEmail({ email, code: code.trim() })
      await qc.invalidateQueries({ queryKey: ['me'] })
      toast.success('Email verified. Welcome in.')
      navigate('/', { replace: true })
    } catch (err) {
      toast.error((err as ApiError).message || 'That code didn’t work.')
    } finally {
      setBusy(false)
    }
  }

  async function resendLoginCode() {
    try {
      await api.auth.resendVerification({ email })
      toast.success(`A new code is on its way to ${email}.`)
    } catch (err) {
      toast.error((err as ApiError).message || 'Could not resend the code.')
    }
  }

  async function sendReset(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    try {
      await api.auth.forgotPassword({ email })
      setSent(true)
    } catch (err) {
      toast.error((err as ApiError).message || 'Could not send the reset link.')
    } finally {
      setBusy(false)
    }
  }

  if (emailVerify) {
    return (
      <div className="auth-wrap">
        <AuthAside />
        <div ref={ref} className="auth-card" style={{ maxWidth: 460 }}>
          <h1 className="anim">Verify your email</h1>
          <p className="lede anim">
            Your email isn’t verified yet. We sent a 6-digit code to <strong>{email}</strong> —
            enter it to continue.
          </p>
          <form className="card" onSubmit={verifyEmailNow}>
            <div className="field anim">
              <label htmlFor="lv-code">Verification code</label>
              <CodeInput id="lv-code" value={code} onChange={setCode} autoFocus />
            </div>
            <button className="anim" type="submit" disabled={busy || code.length !== 6} style={{ width: '100%' }}>
              {busy ? 'Verifying…' : 'Verify & continue'}
            </button>
            <p className="small muted anim" style={{ marginTop: 14, textAlign: 'center' }}>
              Didn’t receive it? <button type="button" className="linklike" onClick={resendLoginCode}>Resend code</button>
            </p>
            <p className="small muted anim" style={{ marginTop: 6, textAlign: 'center' }}>
              <a href="#" onClick={ev => { ev.preventDefault(); setEmailVerify(false) }}>Back to sign in</a>
            </p>
          </form>
        </div>
      </div>
    )
  }

  if (forgot) {
    return (
      <div className="auth-wrap">
        <AuthAside />
        <div ref={ref} className="auth-card">
          <h1 className="anim">Reset password</h1>
          <p className="lede anim">We’ll email you a reset link.</p>
          {sent ? (
            <div className="card">
              <div className="alert ok">
                If an account exists for <strong>{email}</strong>, a reset link is on its way.
                The link is valid for a short time.
              </div>
              <button className="secondary" style={{ width: '100%' }}
                      onClick={() => { setForgot(false); setSent(false) }}>
                Back to sign in
              </button>
            </div>
          ) : (
            <form className="card" onSubmit={sendReset}>
              <div className="field anim">
                <label htmlFor="fe">Email</label>
                <input id="fe" type="email" autoComplete="username" required
                       value={email} onChange={e => setEmail(e.target.value)} />
              </div>
              <button className="anim" type="submit" disabled={busy} style={{ width: '100%' }}>
                {busy ? 'Sending…' : 'Send reset link'}
              </button>
              <p className="small muted anim" style={{ marginTop: 16, marginBottom: 0 }}>
                <a href="#" onClick={e => { e.preventDefault(); setForgot(false) }}>Back to sign in</a>
              </p>
            </form>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="auth-wrap">
        <AuthAside />
      <div ref={ref} className="auth-card">
        <h1 className="anim">Welcome back</h1>
        <p className="lede anim">Sign in to your ItsBingMyBusiness account.</p>
        <form className="card" onSubmit={submit}>
          <div className="field anim">
            <label htmlFor="email">Email</label>
            <input id="email" type="email" autoComplete="username" required
                   value={email} onChange={e => setEmail(e.target.value)} />
          </div>
          <div className="field anim">
            <div className="spread" style={{ marginBottom: 6 }}>
              <label htmlFor="pw" style={{ marginBottom: 0 }}>Password</label>
              <a href="#" className="small" onClick={e => { e.preventDefault(); setForgot(true) }}>Forgot password?</a>
            </div>
            <PasswordInput id="pw" autoComplete="current-password" value={password} onChange={setPassword} />
          </div>
          <label className="auth-check anim">
            <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} />
            <span>Keep me signed in <span className="muted">for 30 days</span></span>
          </label>
          {needCode && (
            <div className="field anim">
              <label htmlFor="mfa">Authenticator code</label>
              <CodeInput id="mfa" value={code} onChange={setCode} autoFocus />
              <div className="hint">Two-factor is on for this account. Enter the 6-digit code.</div>
            </div>
          )}
          <button className="anim" type="submit" disabled={busy || (needCode && code.length !== 6)} style={{ width: '100%' }}>
            {busy ? 'Signing in…' : needCode ? 'Verify & sign in' : 'Sign in'}
          </button>
          <p className="small muted anim" style={{ marginTop: 16, marginBottom: 0 }}>
            No account? <Link to="/signup">Create one</Link>
          </p>
        </form>
      </div>
    </div>
  )
}

/** Landing page for the reset link in the email: ?token=... → set a new password. */
export function ResetPassword() {
  const ref = useAuthAnimation()
  const navigate = useNavigate()
  const toast = useToast()
  const token = new URLSearchParams(window.location.search).get('token') ?? ''
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    try {
      await api.auth.resetPassword({ token, newPassword: password })
      setDone(true)
      toast.success('Your password has been reset.')
    } catch (err) {
      toast.error((err as ApiError).message || 'Could not reset the password.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth-wrap">
        <AuthAside />
      <div ref={ref} className="auth-card">
        <h1 className="anim">Set a new password</h1>
        {!token && <div className="card"><div className="alert error">This reset link is missing its token. Request a new one from the sign-in page.</div></div>}
        {token && done && (
          <div className="card">
            <div className="alert ok">Your password has been reset. You can sign in now.</div>
            <button className="secondary" style={{ width: '100%' }} onClick={() => navigate('/login', { replace: true })}>
              Go to sign in
            </button>
          </div>
        )}
        {token && !done && (
          <form className="card" onSubmit={submit}>
            <div className="field anim">
              <label htmlFor="rp">New password</label>
              <PasswordInput id="rp" autoComplete="new-password" minLength={12} value={password} onChange={setPassword} />
              <div className="hint">At least 12 characters.</div>
            </div>
            <button className="anim" type="submit" disabled={busy || password.length < 12} style={{ width: '100%' }}>
              {busy ? 'Saving…' : 'Reset password'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

// Comprehensively sanctioned jurisdictions we do not serve. Kept in step with KycRules.cs.
const SANCTIONED = new Set(['IR', 'KP', 'SY', 'CU'])
const regionNames = (() => {
  try { return new Intl.DisplayNames(['en'], { type: 'region' }) } catch { return null }
})()
const COUNTRIES = getCountries()
  .filter(c => !SANCTIONED.has(c))
  .map(c => ({ code: c, name: regionNames?.of(c) ?? c, dial: getCountryCallingCode(c) }))
  .sort((a, b) => a.name.localeCompare(b.name))

function guessCountry(): string {
  const tag = (navigator.languages?.[0] ?? navigator.language ?? '').split('-')[1]?.toUpperCase()
  return tag && COUNTRIES.some(c => c.code === tag) ? tag : ''
}

export function Signup() {
  const ref = useAuthAnimation()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const toast = useToast()
  const [form, setForm] = useState({
    email: '', password: '', companyName: '', website: '',
    useCase: '', expectedVolume: '', acceptTerms: false,
    accountType: '' as '' | 'individual' | 'company', contactName: '',
    country: guessCountry(), phone: '',
  })
  const [busy, setBusy] = useState(false)
  // Email verification is a hard gate: signup no longer logs you in — you must enter the
  // emailed code first. 'form' collects details; 'code' verifies the address.
  const [stage, setStage] = useState<'form' | 'code'>('form')
  const [code, setCode] = useState('')

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm(f => ({
      ...f,
      [k]: (e.target as HTMLInputElement).type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value,
    }))

  const pwTooShort = form.password.length > 0 && form.password.length < 12
  const dial = form.country ? getCountryCallingCode(form.country as CountryCode) : ''
  const phoneOk = !!form.country && !!form.phone && isValidPhoneNumber(form.phone, form.country as CountryCode)
  const phoneBad = form.phone.length > 3 && !!form.country && !phoneOk
  const isCompany = form.accountType === 'company'

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.accountType) { toast.error('Choose Individual or Company.'); return }
    if (!phoneOk) { toast.error('Enter a valid phone number for the selected country.'); return }
    setBusy(true)
    try {
      await api.auth.signup({
        email: form.email,
        password: form.password,
        companyName: form.companyName,
        website: form.website || undefined,
        useCase: form.useCase || undefined,
        expectedVolume: form.expectedVolume ? Number(form.expectedVolume) : undefined,
        acceptTerms: form.acceptTerms,
        accountType: form.accountType,
        country: form.country,
        phone: form.phone,
        contactName: isCompany ? form.contactName || undefined : undefined,
      })
      // No session yet — move to the code step.
      setStage('code')
      toast.success(`We’ve sent a 6-digit code to ${form.email}.`)
    } catch (err) {
      const e2 = err as ApiError
      // If they already have an account, guide them to sign in rather than just erroring.
      if (e2.code === 'email_in_use') {
        toast.error('You already have an account with this email — please sign in instead.')
      } else {
        toast.error(e2.message || 'Could not create the account.')
      }
    } finally {
      setBusy(false)
    }
  }

  async function verify(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    try {
      await api.auth.verifyEmail({ email: form.email, code: code.trim() })
      await qc.invalidateQueries({ queryKey: ['me'] })
      toast.success('Email verified. Setting up your account…')
      navigate('/', { replace: true })
    } catch (err) {
      toast.error((err as ApiError).message || 'That code didn’t work.')
    } finally {
      setBusy(false)
    }
  }

  async function resend() {
    try {
      await api.auth.resendVerification({ email: form.email })
      toast.success(`A new code is on its way to ${form.email}.`)
    } catch (err) {
      toast.error((err as ApiError).message || 'Could not resend the code.')
    }
  }

  if (stage === 'code') {
    return (
      <div className="auth-wrap">
        <AuthAside />
        <div ref={ref} className="auth-card" style={{ maxWidth: 460 }}>
          <h1 className="anim">Verify your email</h1>
          <p className="lede anim">
            We sent a 6-digit code to <strong>{form.email}</strong>. Enter it to finish
            creating your account.
          </p>
          <form className="card" onSubmit={verify}>
            <div className="field anim">
              <label htmlFor="su-code">Verification code</label>
              <CodeInput id="su-code" value={code} onChange={setCode} autoFocus />
            </div>
            <button className="anim" type="submit" disabled={busy || code.length !== 6} style={{ width: '100%' }}>
              {busy ? 'Verifying…' : 'Verify & continue'}
            </button>
            <p className="small muted anim" style={{ marginTop: 14, textAlign: 'center' }}>
              Didn’t receive it? <button type="button" className="linklike" onClick={resend}>Resend code</button>
            </p>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div className="auth-wrap">
        <AuthAside />
      <div ref={ref} className="auth-card" style={{ maxWidth: 520 }}>
        <h1 className="anim">Create an account</h1>
        <p className="lede anim">
          Three steps: verify your email, upload your identity documents, and our compliance team
          approves the account. You can set up your domains while you wait.
        </p>
        <form className="card" onSubmit={submit}>
          <div className="field anim">
            <label>Account type</label>
            <div className="segmented" role="radiogroup" aria-label="Account type">
              <button type="button" role="radio" aria-checked={form.accountType === 'individual'}
                      className={form.accountType === 'individual' ? 'active' : ''}
                      onClick={() => setForm(f => ({ ...f, accountType: 'individual' }))}>Individual</button>
              <button type="button" role="radio" aria-checked={form.accountType === 'company'}
                      className={form.accountType === 'company' ? 'active' : ''}
                      onClick={() => setForm(f => ({ ...f, accountType: 'company' }))}>Company</button>
            </div>
            <div className="hint">
              {isCompany
                ? 'You will upload the company registration, a director’s ID and proof of address. Approved companies start at 250 emails/day.'
                : 'You will upload a government photo ID and a selfie holding it. Approved individuals start at 100 emails/day.'}
            </div>
          </div>

          <div className="field anim">
            <label htmlFor="company">{isCompany ? 'Registered company name' : 'Full name'}</label>
            <input id="company" required value={form.companyName} onChange={set('companyName')}
                   autoComplete={isCompany ? 'organization' : 'name'} />
          </div>

          {isCompany && (
            <div className="field anim">
              <label htmlFor="contact">Your name <span className="muted">(contact person)</span></label>
              <input id="contact" required value={form.contactName} onChange={set('contactName')} autoComplete="name" />
            </div>
          )}

          <div className="field anim">
            <label htmlFor="country">Country</label>
            <select id="country" required value={form.country} onChange={set('country') as any}>
              <option value="">Select your country…</option>
              {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name} (+{c.dial})</option>)}
            </select>
          </div>

          <div className="field anim">
            <label htmlFor="phone">Phone number</label>
            <div className="row" style={{ gap: 8 }}>
              <span className="mono small muted" style={{ minWidth: 44 }}>{dial ? `+${dial}` : '+'}</span>
              <input id="phone" type="tel" required autoComplete="tel-national" inputMode="tel"
                     placeholder={form.country ? 'Number without the country code' : 'Choose a country first'}
                     disabled={!form.country} value={form.phone} onChange={set('phone')} />
            </div>
            <div className="hint" style={{ color: phoneBad ? 'var(--danger)' : undefined }}>
              {phoneBad ? 'That is not a valid number for the selected country.' : 'We use it only for account security and compliance checks.'}
            </div>
          </div>

          <div className="field anim">
            <label htmlFor="su-email">Work email</label>
            <input id="su-email" type="email" autoComplete="username" required
                   value={form.email} onChange={set('email')} />
          </div>

          <div className="field anim">
            <label htmlFor="su-pw">Password</label>
            <PasswordInput id="su-pw" autoComplete="new-password" minLength={12}
                           value={form.password} onChange={v => setForm(f => ({ ...f, password: v }))} />
            <div className="hint" style={{ color: pwTooShort ? 'var(--danger)' : undefined }}>
              At least 12 characters. Length beats complexity rules.
            </div>
          </div>

          <div className="field anim">
            <label htmlFor="site">Website</label>
            <input id="site" required placeholder="https://example.com" value={form.website} onChange={set('website')} />
            <div className="hint">
              Required for every account — it is the first thing our reviewers check.
            </div>
          </div>

          <div className="field anim">
            <label htmlFor="usecase">What will you send? <span className="muted">(optional)</span></label>
            <select id="usecase" value={form.useCase} onChange={set('useCase') as any}>
              <option value="">Select the closest match…</option>
              <option value="Password resets & account emails">Password resets & account emails</option>
              <option value="Order & payment confirmations">Order & payment confirmations</option>
              <option value="Shipping & delivery updates">Shipping & delivery updates</option>
              <option value="OTP / 2FA verification codes">OTP / 2FA verification codes</option>
              <option value="App notifications & alerts">App notifications & alerts</option>
              <option value="Invoices & billing">Invoices & billing</option>
              <option value="Newsletters & product updates">Newsletters & product updates</option>
              <option value="Marketing & promotions">Marketing & promotions</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="field anim">
            <label htmlFor="vol">Expected messages per day <span className="muted">(optional)</span></label>
            <select id="vol" value={form.expectedVolume} onChange={set('expectedVolume') as any}>
              <option value="">Select a range…</option>
              <option value="100">Under 100</option>
              <option value="1000">100 – 1,000</option>
              <option value="10000">1,000 – 10,000</option>
              <option value="50000">10,000 – 50,000</option>
              <option value="100000">50,000+</option>
            </select>
          </div>

          <div className="field anim">
            <label className="row" style={{ fontWeight: 400, fontSize: 13.5 }}>
              <input type="checkbox" style={{ width: 16, height: 16 }}
                     checked={form.acceptTerms} onChange={set('acceptTerms')} required />
              <span>
                I accept the <a href="https://itsbingmybusiness.com/terms" target="_blank" rel="noreferrer">Terms</a>,{' '}
                <a href="https://itsbingmybusiness.com/privacy" target="_blank" rel="noreferrer">Privacy Policy</a> and{' '}
                <a href="https://itsbingmybusiness.com/acceptable-use" target="_blank" rel="noreferrer">Acceptable Use Policy</a>.
                Purchased, rented and scraped recipient lists are prohibited.
              </span>
            </label>
          </div>

          <button className="anim" type="submit" disabled={busy || !form.acceptTerms || !form.accountType}
                  style={{ width: '100%' }}>
            {busy ? 'Creating…' : 'Create account'}
          </button>

          <p className="small muted anim" style={{ marginTop: 16, marginBottom: 0 }}>
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  )
}

/** Password field with a show/hide toggle. */
function PasswordInput({ id, value, onChange, autoComplete, minLength }: {
  id: string; value: string; onChange: (v: string) => void; autoComplete: string; minLength?: number
}) {
  const [show, setShow] = useState(false)
  return (
    <div className="pw-wrap">
      <input id={id} type={show ? 'text' : 'password'} autoComplete={autoComplete} required minLength={minLength}
             value={value} onChange={e => onChange(e.target.value)} />
      <button type="button" className="pw-toggle" onClick={() => setShow(s => !s)}
              aria-label={show ? 'Hide password' : 'Show password'} aria-pressed={show}>
        {show ? (
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/><path d="m1 1 22 22"/></svg>
        ) : (
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
        )}
      </button>
    </div>
  )
}
